/* Signet Reviews — storefront widget. Self-contained, no dependencies.
 * Reads approved reviews from the public reviews API and renders:
 *   • Product widget  — <div data-signet-reviews data-product-id="{{ product.id }}"></div>
 *   • Card badge      — <span data-signet-review-badge data-product-id="{{ product.id }}"></span>
 *   • Summary/trust   — <div data-signet-reviews-summary data-limit="12"></div>
 * Override the API host if needed via window.SIGNET_REVIEWS_API before this script loads.
 */
(function () {
  'use strict';
  var API = window.SIGNET_REVIEWS_API || 'https://reviews.collectsignet.com/.netlify/functions/reviews-widget';

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function get(url) { return fetch(url, { credentials: 'omit' }).then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); }); }

  // A 5-star row. `value` fills fractionally (for averages); integers render cleanly too.
  function stars(value) {
    var pct = Math.max(0, Math.min(100, (Number(value) || 0) / 5 * 100));
    return '<span class="sgr-stars" aria-label="' + (Math.round((value || 0) * 10) / 10) + ' out of 5">' +
      '<span class="sgr-stars-empty">★★★★★</span>' +
      '<span class="sgr-stars-fill" style="width:' + pct + '%">★★★★★</span></span>';
  }

  function fmtDate(iso) {
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }); }
    catch (e) { return ''; }
  }

  // ---- Photo lightbox (shared) ----
  // Takes the review the photo came from so the words travel with the picture. A photo of a
  // signed item is evidence; without the review beside it the viewer has to close, find the
  // card again and re-read it to learn what the customer actually said.
  // `idx` picks the matching ORIGINAL when the caller passes a thumbnail: tiles are drawn
  // from display-sized copies, but the lightbox is where the detail is the point.
  /**
   * Nudge the trust rail along on a timer.
   *
   * ⚠ Auto-motion is easy to get wrong, so all four brakes are deliberate:
   *   • PAUSES while the pointer is over the rail — nobody should have their reading moved.
   *   • STOPS PERMANENTLY once a person scrolls it or uses an arrow. They have taken over;
   *     a carousel that keeps fighting the user is worse than one that never moved.
   *   • OFF entirely for `prefers-reduced-motion`. Not negotiable — this is vestibular, not taste.
   *   • OFF when the tab is hidden, so a background tab isn't silently scrolling.
   * ⚠ There is no hover on touch, so the pause can't apply there — the interaction brake is
   *   what covers mobile, and a swipe stops it for good.
   */
  function autoAdvance(row, enabled, seconds) {
    if (!row || !enabled) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var timer = null, stopped = false, paused = false;

    function step() {
      if (stopped || paused || document.hidden) return;
      var card = row.querySelector('.sgr-mini');
      var by = card ? card.getBoundingClientRect().width + 14 : Math.round(row.clientWidth * 0.8);
      // At the end, go back to the start rather than sitting still — a rail parked on its
      // last card looks broken, and the loop is what makes it read as a carousel.
      var atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 8;
      row.scrollTo({ left: atEnd ? 0 : row.scrollLeft + by, behavior: 'smooth' });
    }

    function stop() {
      stopped = true;
      if (timer) { clearInterval(timer); timer = null; }
    }

    timer = setInterval(step, seconds * 1000);
    row.addEventListener('mouseenter', function () { paused = true; });
    row.addEventListener('mouseleave', function () { paused = false; });
    // ⚠ `wheel`/`touchstart` rather than `scroll`: our own smooth scrolling fires `scroll`
    // too, so listening for that would stop the timer on its very first tick.
    ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(function (ev) {
      row.addEventListener(ev, stop, { passive: true });
    });
  }

  /**
   * @param {string|null} src   photo to show, or null for a review with no photo
   * @param {object} r          the review
   * @param {number} [idx]      which of that review's photos
   * @param {{list:object[],index:number}} [nav]  optional siblings to arrow between
   */
  function lightbox(src, r, idx, nav) {
    var list = (nav && Array.isArray(nav.list) && nav.list.length > 1) ? nav.list : null;
    var at = list ? (Number(nav.index) || 0) : 0;

    var o = document.createElement('div');
    o.className = 'sgr-lightbox';
    o.setAttribute('role', 'dialog');
    o.setAttribute('aria-modal', 'true');

    // ⛔ The panel is REPAINTED in place rather than torn down and rebuilt, so arrowing
    // between reviews doesn't flash the backdrop or lose focus each time.
    function paint(review, photo) {
      // ⚠ A review with NO photo now reaches here — the whole card is clickable, not just the
      // image. Rendering <img src=""> would draw a broken-image glyph, so the image is
      // conditional and the panel simply becomes text-only.
      if (review && review.photos_full && review.photos_full.length) {
        var i = typeof idx === 'number' ? idx : (review.photos || []).indexOf(photo);
        photo = review.photos_full[i > -1 ? i : 0] || photo;
      } else if (!photo && review && review.photos && review.photos.length) {
        photo = review.photos[0];
      }
      var detail = '';
      if (review) {
        detail = '<div class="sgr-lb-detail">' +
          (review.signer ? '<div class="sgr-lb-signer">' + esc(review.signer) + eventTag(review) + '</div>' : '') +
          '<div class="sgr-lb-head">' + stars(review.rating) +
            '<span class="sgr-lb-name">' + esc(review.name || '') + '</span>' +
            (review.verified ? '<span class="sgr-badge sgr-verified">Verified buyer</span>' : '') +
            '<span class="sgr-date">' + esc(fmtDate(review.date)) + '</span></div>' +
          (review.title ? '<div class="sgr-lb-title">' + esc(review.title) + '</div>' : '') +
          (review.body ? '<div class="sgr-lb-body">' + esc(review.body) + '</div>' : '') +
          '</div>';
      }
      o.setAttribute('aria-label', (photo ? 'Customer photo' : 'Customer review') +
        (review && review.signer ? ' — ' + review.signer : ''));
      // ⛔ The arrows and counter are siblings of the panel, NOT children of it. `.sgr-lb-inner`
      // is `overflow: hidden`, so anything positioned outside its box is clipped away — which
      // is exactly why no arrows appeared when they were nested inside it.
      o.innerHTML =
        (list ? '<button class="sgr-lb-nav sgr-lb-prev" aria-label="Previous review">‹</button>' : '') +
        '<div class="sgr-lb-inner' + (photo ? '' : ' sgr-lb-inner--notext') + '">' +
          '<button class="sgr-lb-x" aria-label="Close">×</button>' +
          (photo ? '<img src="' + esc(photo) + '" alt="Customer photo of a signed item" />' : '') +
          detail +
        '</div>' +
        (list ? '<button class="sgr-lb-nav sgr-lb-next" aria-label="Next review">›</button>' : '') +
        (list ? '<div class="sgr-lb-count">' + (at + 1) + ' / ' + list.length + '</div>' : '');
      wire();
    }

    function go(delta) {
      if (!list) return;
      at = (at + delta + list.length) % list.length;
      idx = undefined;                       // a new entry means its own first photo
      // ⚠ Two shapes arrive here. The trust rail passes REVIEWS; the photo wall passes TILES
      // ({src, review}) because one review can contribute several photos and the wall shows
      // each separately — stepping review-by-review there would skip photos the person can see.
      var it = list[at];
      if (it && it.review) paint(it.review, it.src);
      else paint(it, null);
      var btn = o.querySelector('.sgr-lb-x');
      if (btn) btn.focus();
    }

    // A modal that can't be escaped or tabbed out of is the most common accessibility failure
    // in a review widget. Remember what had focus, move focus in, keep Tab inside while it's
    // open, and hand focus back on close.
    var opener = document.activeElement;
    var prevOverflow = document.body.style.overflow;

    function close() {
      if (o.parentNode) document.body.removeChild(o);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (opener && opener.focus) opener.focus();
    }

    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (list && e.key === 'ArrowLeft')  { go(-1); return; }
      if (list && e.key === 'ArrowRight') { go(1);  return; }
      if (e.key !== 'Tab') return;
      // Keep Tab inside the panel, cycling whatever controls this render has.
      var f = o.querySelectorAll('.sgr-lb-x, .sgr-lb-nav');
      if (!f.length) return;
      e.preventDefault();
      var cur = Array.prototype.indexOf.call(f, document.activeElement);
      var next = e.shiftKey ? cur - 1 : cur + 1;
      f[(next + f.length) % f.length].focus();
    }

    function wire() {
      var prev = o.querySelector('.sgr-lb-prev');
      var next = o.querySelector('.sgr-lb-next');
      // stopPropagation: without it the backdrop handler below sees the click and closes.
      if (prev) prev.addEventListener('click', function (e) { e.stopPropagation(); go(-1); });
      if (next) next.addEventListener('click', function (e) { e.stopPropagation(); go(1); });
    }

    paint(r, src);
    // Click the backdrop or the × to close — but not the panel, so the text stays selectable.
    o.addEventListener('click', function (e) {
      if (e.target === o || (e.target.className || '') === 'sgr-lb-x') close();
    });
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';      // no scrolling the page behind the modal
    document.body.appendChild(o);
    o.querySelector('.sgr-lb-x').focus();
  }

  // Emptying the widget's own div isn't enough — the Liquid wrapper around it carries the
  // section margins, so a failed load would leave a silent ~80px hole between the product
  // details and whatever follows. Hide the wrapper, and the page closes up as though the
  // section were never there.
  function collapse(el) {
    el.innerHTML = '';
    var wrap = el.closest && el.closest('.signet-reviews-section, .signet-reviews-summary, .signet-reviews-all');
    if (wrap) wrap.style.display = 'none';
  }

  // A placeholder shaped like what's coming, so nothing below it moves when the reviews land.
  // "Loading…" is one line tall; the real content is several hundred pixels, and the gap between
  // the two is the page visibly jumping under whoever is already reading it.
  function skeleton(rows, o) {
    o = o || {};
    var out = '<div class="sgr-skels" aria-hidden="true">';
    if (o.bar !== false) out += '<div class="sgr-skel sgr-skel-bar"></div>';
    if (o.rail) out += '<div class="sgr-skel sgr-skel-rail"></div>';
    for (var i = 0; i < (rows || 3); i++) out += '<div class="sgr-skel sgr-skel-row"></div>';
    // Screen readers get told what's happening; the shapes are decoration and stay hidden.
    return out + '</div><span class="sgr-sr" role="status">Loading reviews…</span>';
  }

  // Shared horizontal-rail behaviour: arrows that hide themselves at each end.
  // Used by the photo strip and the signer faces so they feel like the same control.
  //
  // Deliberately NO wheel handling. Translating vertical wheel into horizontal scroll means
  // preventDefault()ing the page scroll, so anyone scrolling PAST the rail gets caught by it
  // until it reaches its end — a scroll trap. Arrows, touch swipe and shift+wheel all still
  // work, and none of them hijack an interaction the visitor didn't aim at the rail.
  function wireRail(strip, L, R) {
    function sync() {
      var max = strip.scrollWidth - strip.clientWidth;
      L.classList.toggle('sgr-hide', strip.scrollLeft <= 2);
      R.classList.toggle('sgr-hide', strip.scrollLeft >= max - 2 || max <= 0);
    }
    function nudge(dir) { strip.scrollBy({ left: dir * Math.max(176, strip.clientWidth * 0.8), behavior: 'smooth' }); }
    L.addEventListener('click', function () { nudge(-1); });
    R.addEventListener('click', function () { nudge(1); });
    strip.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    sync();
  }

  // ---- Product widget ----
  // `showSigner` is for the fill block, where the reviews are about OTHER people and would
  // otherwise read as though they were about the signer whose page this is.
  function reviewCard(r, showSigner) {
    var photos = (r.photos || []).map(function (p) {
      return '<img class="sgr-photo" src="' + esc(p) + '" alt="Customer photo" loading="lazy" />';
    }).join('');
    var badges = '';
    if (r.verified) badges += '<span class="sgr-badge sgr-verified">Verified buyer</span>';
    if (r.incentivized) badges += '<span class="sgr-badge sgr-inc">Incentivized</span>';
    return '<div class="sgr-review">' +
      (showSigner && r.signer ? '<div class="sgr-rev-signer">' + esc(r.signer) + '</div>' : '') +
      '<div class="sgr-review-head">' + stars(r.rating) +
        '<span class="sgr-name">' + esc(r.name) + '</span>' +
        '<span class="sgr-date">' + esc(fmtDate(r.date)) + '</span>' +
        eventTag(r) + '</div>' +
      (r.title ? '<div class="sgr-title">' + esc(r.title) + '</div>' : '') +
      (r.body ? '<div class="sgr-body">' + esc(r.body) + '</div>' : '') +
      (photos ? '<div class="sgr-photos">' + photos + '</div>' : '') +
      (badges ? '<div class="sgr-badges">' + badges + '</div>' : '') +
      (r.reply ? '<div class="sgr-reply"><span class="sgr-reply-by">Signet responded</span>' + esc(r.reply) + '</div>' : '') +
      '</div>';
  }

  // A button, not a div: the bars are the product page's rating filter, which is the natural
  // place to reach for one. Rows with no reviews aren't clickable — nothing to show.
  function distRow(star, n, total) {
    var pct = total ? Math.round(n / total * 100) : 0;
    return '<button class="sgr-dist-row" data-star="' + star + '"' + (n ? '' : ' disabled') +
      ' aria-pressed="false" aria-label="Show only ' + star + '-star reviews">' +
      '<span class="sgr-dist-label">' + star + '★</span>' +
      '<span class="sgr-dist-bar"><span style="width:' + pct + '%"></span></span>' +
      '<span class="sgr-dist-n">' + n + '</span></button>';
  }

  // schema.org Product + AggregateRating, so review stars can show in Google results.
  //
  // This is the one piece of the old review app that is invisible on the page but valuable off
  // it — dropping it would quietly cost star snippets sitewide. It goes on PRODUCT pages only:
  // Google no longer grants rich results to self-serving Organization reviews, so marking up
  // the all-reviews page would earn nothing.
  //
  // Injected after the ratings load, since only the API knows them. One node per product, and
  // only when there's at least one review — aggregateRating with a zero count is invalid.
  function injectSeo(el, d) {
    if (el.getAttribute('data-seo') === 'false') return;
    var name = el.getAttribute('data-product-name');
    var url = el.getAttribute('data-product-url');
    if (!name || !d.count || d.average == null) return;
    if (document.querySelector('script[data-sgr-seo="' + el.getAttribute('data-product-id') + '"]')) return;

    var node = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: name,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: String(d.average),
        reviewCount: String(d.count),
        bestRating: '5', worstRating: '1'
      },
      review: (d.reviews || []).slice(0, 10).map(function (r) {
        var o = {
          '@type': 'Review',
          reviewRating: { '@type': 'Rating', ratingValue: String(r.rating), bestRating: '5', worstRating: '1' },
          author: { '@type': 'Person', name: r.name || 'Verified buyer' },
          datePublished: String(r.date || '').slice(0, 10)
        };
        if (r.title) o.name = r.title;
        if (r.body) o.reviewBody = r.body;
        return o;
      })
    };
    if (url) node.url = url;
    var img = el.getAttribute('data-product-image');
    if (img) node.image = img;

    var s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-sgr-seo', el.getAttribute('data-product-id'));
    // textContent, never innerHTML: review bodies are customer-supplied.
    s.textContent = JSON.stringify(node);
    document.head.appendChild(s);
  }

  // Shared bar builder, so the has-reviews and no-reviews states are the same object with
  // different numbers rather than one designed layout and one paragraph of text.
  // ONE header for every surface. The product page and the landing page render the same
  // element from the same function — the only differences are the numbers and whether there's
  // a face to show. Anything else drifts the moment one of them is tweaked.
  //
  // `face`: a URL for an avatar, or false for no identity image (the landing page).
  function statsBar(title, sub, avg, figures, face) {
    return '<div class="sgr-statsbar">' +
      '<div class="sgr-stats-id' + (face !== false ? ' sgr-stats-id-signer' : '') + '">' +
        (face !== false ? avatar(title, face || null, 'sgr-face-md') : '') +
        // Omit the element entirely when there's no subtitle — an empty span still occupies a
        // line and leaves the name sitting oddly high in the bar.
        '<span><span class="sgr-stats-title">' + esc(title) + '</span>' +
        (sub ? '<span class="sgr-stats-sub">' + esc(sub) + '</span>' : '') + '</span>' +
      '</div>' +
      '<div class="sgr-stats-set">' +
        '<div class="sgr-stat-avg"><div class="sgr-stat-avg-row">' +
          '<span class="sgr-stat-stars">' + stars(avg) + '</span>' +
          '<span class="sgr-stat-numwrap">' +
            '<span class="sgr-stat-num">' + (avg != null ? Number(avg).toFixed(2) : '—') + '</span>' +
            '<span class="sgr-stat-lbl">average rating</span>' +
          '</span>' +
        '</div></div>' +
        figures.map(function (f) {
          return '<div class="sgr-stat-div"></div><div class="sgr-stat">' +
            '<div class="sgr-stat-num">' + f.n + '</div>' +
            '<div class="sgr-stat-lbl">' + esc(f.label) + '</div></div>';
        }).join('') +
      '</div>' +
    '</div>';
  }

  function renderProduct(el) {
    var id = el.getAttribute('data-product-id');
    if (!id) return;
    var signer = (el.getAttribute('data-signer') || '').trim();
    var fill = Number(el.getAttribute('data-fill')) || 0;
    // Deep-link to the full page, pre-filtered to this signer, for anyone who wants more than
    // the handful shown here.
    var allPage = (el.getAttribute('data-all-url') || '').trim();
    var allUrl = allPage ? (allPage + (signer ? (allPage.indexOf('?') === -1 ? '?' : '&') + 'signer=' + encodeURIComponent(signer) : '')) : '';

    // Products rotate every event, so reviews keyed to THIS listing strand the signer's
    // history — a fresh pre-order page shows zero for someone with seventeen reviews. Ask for
    // the person when the theme knows who they are (the signet.signer_name metafield), and
    // fall back to deriving them from a product that already has reviews. Only a caller that
    // already knows the name can rescue a brand-new listing: it has no reviews and no stats
    // row, so there's nothing to infer from.
    var url;
    if (signer) {
      url = API + '?signer=' + encodeURIComponent(signer) + '&product_id=' + encodeURIComponent(id) +
            (fill ? '&fill=' + fill : '');
    } else {
      url = API + '?product_id=' + encodeURIComponent(id) + '&expand=signer' + (fill ? '&fill=' + fill : '');
    }

    el.innerHTML = skeleton(3);
    get(url).then(function (d) {
      var reviews = d.reviews || [];
      var others = d.others || [];
      if (!reviews.length && !others.length) {
        el.innerHTML = '<div class="sgr-empty">No reviews yet — be the first after your order arrives.</div>';
        return;
      }

      // Same gradient bar as the landing page, so the two surfaces read as one system. The
      // signer's face and name are the identity block, which is also why the rows below can
      // drop them.
      var head = '';
      if (d.count) {
        var dist = d.distribution || {};
        // No "across multiple signings" line. The show logo on each review already says which
        // event it came from, so the sentence was labelling something the cards demonstrate.
        var sub = d.signer ? 'Reviews for ' + d.signer : 'Verified buyers, in their own words';
        head =
          statsBar(
            d.signer || 'Customer Reviews', sub, d.average,
            [{ n: d.count, label: 'review' + (d.count === 1 ? '' : 's') }],
            d.headshot
          ) +
          // Distribution stays — it's standard on a product page and dropping it would be a
          // regression from what shoppers expect.
          '<div class="sgr-dist sgr-dist-block">' +
            [5, 4, 3, 2, 1].map(function (s) { return distRow(s, dist[s] || 0, d.count); }).join('') +
          '</div>';
      }

      // A product page is a quick hit, not a reading session — show a few, let anyone who
      // wants more expand in place, and send the genuinely curious to the full page. Loading
      // 17 reviews with photos into a page someone is trying to buy from is the wrong trade.
      var pageSize = Math.max(2, Number(el.getAttribute('data-shown')) || 4);
      var starFilter = 0;
      var shown = pageSize;

      function visible() {
        return starFilter ? reviews.filter(function (r) { return Number(r.rating) === starFilter; }) : reviews;
      }
      function paint() {
        var v = visible();
        var slice = v.slice(0, shown);
        var listEl = el.querySelector('.sgr-pdp-list');
        var footEl = el.querySelector('.sgr-pdp-foot');
        if (!listEl) return;
        listEl.innerHTML = slice.length
          ? slice.map(function (r, i) { return feedCard(r, {}, reviews.indexOf(v[i]), { hideSigner: true }); }).join('')
          : '<div class="sgr-empty">No ' + starFilter + '-star reviews for this signer.</div>';
        var left = v.length - slice.length;
        footEl.innerHTML =
          (left > 0 ? '<button class="sgr-more sgr-more-sm">Show ' + Math.min(left, pageSize) + ' more</button>' : '') +
          (allUrl ? '<a class="sgr-seeall" href="' + esc(allUrl) + '" target="_blank" rel="noopener">Read all reviews →</a>' : '');
        var more = footEl.querySelector('.sgr-more');
        if (more) more.addEventListener('click', function () { shown += pageSize; paint(); });
        bindPhotos();
      }
      function bindPhotos() {
        el.querySelectorAll('.sgr-rphoto').forEach(function (img) {
          if (img.getAttribute('data-bound')) return;
          img.setAttribute('data-bound', '1');
          img.addEventListener('click', function () {
            lightbox(img.getAttribute('src'), all[Number(img.getAttribute('data-r'))]);
          });
        });
      }

      var list = reviews.length ? '<div class="sgr-pdp-list sgr-feed-list"></div><div class="sgr-pdp-foot"></div>' : '';

      // Other signers are kept visually separate and labelled. The API returns them in their
      // own array precisely so they can't be presented as being about this signer — and here
      // they DO show the signer, because they're about someone else.
      var base = reviews.length;
      var fillBlock = others.length
        ? '<div class="sgr-fill">' +
            '<div class="sgr-fill-lbl">' + (d.count ? 'More from Signet customers' : 'Recent reviews from other signings') + '</div>' +
            '<div class="sgr-fill-list sgr-feed-list"></div>' +
            '<div class="sgr-pdp-foot sgr-fill-foot"></div>' +
          '</div>'
        : '';

      var all = reviews.concat(others);
      el.innerHTML = '<div class="sgr-widget sgr-widget-wide">' + head + list + fillBlock + '</div>';

      // No reviews for this signer yet. Rather than a bare line of text, show the same bar
      // with the SITE-WIDE snapshot — it's honest (clearly labelled as all of Signet, not
      // this signing) and gives a shopper something real instead of an apology. One extra
      // request, and only ever in this state.
      if (!d.count) {
        get(API + '?signers=1').then(function (g) {
          // One figure, not two. The signer count is landing-page context; here it just
          // widened the stats and squeezed the name beside it.
          var bar = statsBar(
            d.signer || 'Customer Reviews',
            // Deliberately blank. Announcing "no reviews yet" on a new signer's page states the
            // weakness out loud; the figures beside it are Signet's and speak for themselves.
            '',
            g.average,
            [{ n: g.total_reviews || 0, label: 'reviews' }],
            d.headshot
          );
          // No link here — the fill block below already ends with one, and offering it twice
          // before anyone has read a review is noise.
          var host = el.querySelector('.sgr-widget');
          if (host) host.insertAdjacentHTML('afterbegin', bar);
        }).catch(function () { /* leave the fill block as-is */ });
      }

      // Distribution bars filter the list. Clicking the active one clears it.
      el.querySelectorAll('.sgr-dist-row').forEach(function (b) {
        b.addEventListener('click', function () {
          var s = Number(b.getAttribute('data-star'));
          starFilter = (starFilter === s) ? 0 : s;
          shown = pageSize;
          el.querySelectorAll('.sgr-dist-row').forEach(function (x) {
            var on = Number(x.getAttribute('data-star')) === starFilter;
            x.classList.toggle('sgr-on', on);
            x.setAttribute('aria-pressed', on ? 'true' : 'false');   // state, not just styling
          });
          paint();
        });
      });
      // The fill pages exactly like the main list — a signer with no reviews of their own
      // shouldn't get a longer wall of other people's than a signer who has some.
      var fillShown = pageSize;
      function paintFill() {
        var host = el.querySelector('.sgr-fill-list');
        var foot = el.querySelector('.sgr-fill-foot');
        if (!host) return;
        host.innerHTML = others.slice(0, fillShown)
          .map(function (r, i) { return feedCard(r, {}, base + i, {}); }).join('');
        var left = others.length - fillShown;
        foot.innerHTML =
          (left > 0 ? '<button class="sgr-more sgr-more-sm">Show ' + Math.min(left, pageSize) + ' more</button>' : '') +
          (allPage ? '<a class="sgr-seeall" href="' + esc(allPage) + '" target="_blank" rel="noopener">Read all reviews →</a>' : '');
        var m = foot.querySelector('.sgr-more');
        if (m) m.addEventListener('click', function () { fillShown += pageSize; paintFill(); });
        bindPhotos();
      }

      if (reviews.length) paint(); else bindPhotos();
      if (others.length) paintFill();
      // Only mark up reviews that are actually about this product's signer — never the fill.
      if (d.count) injectSeo(el, d);
    }).catch(function () { collapse(el); });
  }

  // ---- Collection-card badges (batched) ----
  // Prefers the SIGNER when the card knows one. Product-keyed badges leave a grid of freshly
  // launched pre-orders with no stars at all, which reads as "nobody reviews these" rather
  // than "these are new". Cards without a signer fall back to the product id, so an
  // un-migrated theme keeps working.
  function renderBadges() {
    var nodes = [].slice.call(document.querySelectorAll('[data-signet-review-badge]'));
    if (!nodes.length) return;

    function fill(node, s) {
      if (!s || !s.count) { node.style.display = 'none'; return; }
      node.innerHTML = stars(s.average) + '<span class="sgr-badge-count">(' + s.count + ')</span>';
      node.classList.add('sgr-badge-ready');
    }
    function uniq(a) { return a.filter(function (v, i) { return a.indexOf(v) === i; }); }

    var byName = nodes.filter(function (n) { return (n.getAttribute('data-signer') || '').trim(); });
    var byId = nodes.filter(function (n) { return byName.indexOf(n) === -1 && n.getAttribute('data-product-id'); });

    if (byName.length) {
      var names = uniq(byName.map(function (n) { return n.getAttribute('data-signer').trim(); }));
      get(API + '?names=' + encodeURIComponent(names.join('|'))).then(function (d) {
        var b = d.badges || {};
        byName.forEach(function (n) { fill(n, b[n.getAttribute('data-signer').trim()]); });
      }).catch(function () {});
    }
    if (byId.length) {
      var ids = uniq(byId.map(function (n) { return n.getAttribute('data-product-id'); }));
      get(API + '?ids=' + encodeURIComponent(ids.join(','))).then(function (d) {
        var b = d.badges || {};
        byId.forEach(function (n) { fill(n, b[n.getAttribute('data-product-id')]); });
      }).catch(function () {});
    }
  }

  // ---- Summary / trust (homepage, cart) ----
  // Deliberately the lightest surface: this sits on a homepage or in a cart drawer, where the
  // job is a glance of proof, not reading. Same rail behaviour and attribution as everywhere
  // else, but no filters, no paging, and text clamped.
  function renderSummary(el) {
    var limit = el.getAttribute('data-limit') || 12;
    var heading = el.getAttribute('data-heading') || 'What customers say';
    var allPage = (el.getAttribute('data-all-url') || '').trim();
    var showAverage = el.getAttribute('data-show-average') !== 'false';
    var autoplay = el.getAttribute('data-autoplay') !== 'false';
    var autoSecs = Math.max(3, Number(el.getAttribute('data-autoplay-seconds')) || 5);
    el.innerHTML = skeleton(1, { rail: true });
    get(API + '?summary=1&limit=' + encodeURIComponent(limit)).then(function (d) {
      var items = (d.featured || []);
      if (!items.length) { collapse(el); return; }
      // ⚠ THE WHOLE CARD OPENS THE REVIEW, not just the photo. Operator-corrected 2026-08-22:
      // only the image was clickable, which nobody guesses — and the text is clamped to 3
      // lines, so most cards end in "…" with no visible way to read the rest.
      // A <button> rather than a click handler on <article>: it lands in the tab order and
      // announces itself, which a clickable div does not.
      var cards = items.map(function (r, i) {
        var photo = (r.photos && r.photos[0])
          ? '<img class="sgr-mini-photo" src="' + esc(r.photos[0]) + '" alt="Customer photo" loading="lazy" />'
          : '';
        return '<button type="button" class="sgr-mini" data-r="' + i + '" aria-label="Read the full review from ' + esc(r.name || 'a customer') + '">' + photo +
          '<span class="sgr-mini-body">' +
            '<span class="sgr-mini-top">' + stars(r.rating) + eventTag(r) + '</span>' +
            (r.signer ? '<span class="sgr-mini-signer">' + esc(r.signer) + '</span>' : '') +
            (r.title ? '<span class="sgr-mini-title">' + esc(r.title) + '</span>' : '') +
            (r.body ? '<span class="sgr-mini-text">' + esc(r.body) + '</span>' : '') +
            '<span class="sgr-mini-name">' + esc(r.name) + (r.verified ? ' · Verified buyer' : '') + '</span>' +
          '</span></button>';
      }).join('');

      // ── The rating line ────────────────────────────────────────────────────
      // ⚠ Its own line UNDER the heading, sharing that line with "Read all reviews" —
      // operator-specified. Omitted silently when the aggregate didn't come back, so a
      // failed lookup costs a line rather than the section.
      var avgLine = '';
      if (showAverage && typeof d.average === 'number' && d.total) {
        avgLine = '<div class="sgr-trust-sub">' +
          '<span class="sgr-trust-score">' + stars(d.average) +
            '<strong>' + esc(String(d.average)) + '</strong>' +
            '<span class="sgr-trust-count">from ' + esc(String(d.total)) + ' verified reviews</span>' +
          '</span>' +
          (allPage ? '<a class="sgr-seeall" href="' + esc(allPage) + '">Read all reviews →</a>' : '') +
        '</div>';
      }
      // ⚠ When the rating line is shown it TAKES the "Read all reviews" link, so the link is
      // not rendered twice. The heading then sits alone on its own line.
      el.innerHTML = '<div class="sgr-trust">' +
        '<div class="sgr-trust-head">' +
          '<h2 class="sgr-h">' + esc(heading) + '</h2>' +
          (allPage && !avgLine ? '<a class="sgr-seeall" href="' + esc(allPage) + '">Read all reviews →</a>' : '') +
        '</div>' + avgLine +
        '<div class="sgr-faces-rail">' +
          '<button class="sgr-arrow sgr-trust-arrow-l" aria-label="Scroll left">‹</button>' +
          // A closing tile at the end of the rail. Without it the rail just stops, which reads
          // as "that's all there is" rather than "there's more, elsewhere" — and the only way
          // on was a link up in the header, far from where the eye ends up.
          '<div class="sgr-trust-row">' + cards +
            (allPage ? '<a class="sgr-mini sgr-mini-end" href="' + esc(allPage) + '">' +
              '<span class="sgr-mini-end-icon">→</span>' +
              '<span class="sgr-mini-end-text">Read all reviews</span>' +
              // No count: the summary payload carries only `featured`, and fetching a total
              // for one line of copy isn't worth a second request on a homepage.
              '<span class="sgr-mini-end-sub">From verified buyers</span>' +
            '</a>' : '') +
          '</div>' +
          '<button class="sgr-arrow sgr-trust-arrow-r" aria-label="Scroll right">›</button>' +
        '</div></div>';
      var row = el.querySelector('.sgr-trust-row');
      wireRail(row, el.querySelector('.sgr-trust-arrow-l'), el.querySelector('.sgr-trust-arrow-r'));

      // Any part of a card opens that review, with arrows to move between them once open.
      el.querySelectorAll('.sgr-mini[data-r]').forEach(function (card) {
        card.addEventListener('click', function () {
          lightbox(null, items[Number(card.getAttribute('data-r'))], 0, {
            list: items, index: Number(card.getAttribute('data-r')),
          });
        });
      });

      autoAdvance(row, autoplay, autoSecs);
    }).catch(function () { collapse(el); });
  }

  // What was actually reviewed, e.g. "Signed - Funko Pop! - #1579". Derived from the product
  // title with the signer's name and any trailing show removed: the name is already the card's
  // headline and the show is already the logo, so repeating either just adds noise. Matters
  // most for inventory — an autograph pre-order and a signed Funko read very differently.
  // Allows a combined tail — "… - MCM London & NYCC" is one product covering two shows, and
  // stripping only the last token would leave a dangling "- MCM London &".
  var SHOW = '(?:New York Comic Con|Florida Supercon|MCM Birmingham|MCM London|Voices Against Cancer|NYCC|ECCC|C2E2|Supercon)';
  var SHOW_TOKENS = new RegExp('\\s*[-–]\\s*' + SHOW + '(?:\\s*(?:&|and|\\+|,)\\s*' + SHOW + ')*\\s*$', 'i');
  function itemLabel(r) {
    var t = String(r.product || '').trim();
    if (!t) return '';
    if (r.signer && t.toLowerCase().indexOf(String(r.signer).toLowerCase()) === 0) {
      t = t.slice(String(r.signer).length);
    }
    t = t.replace(/^\s*[-–]\s*/, '');
    t = t.replace(SHOW_TOKENS, '');
    t = t.trim().replace(/\s*[-–]\s*$/, '').trim();
    // If stripping left nothing (the title was only the signer + show), there's nothing to add.
    return t;
  }

  // The show it was signed at. Prefers the event logo, falls back to the proper event name —
  // a raw tag like "supercon-2026" should never reach a customer. Logos vary from 1.8:1 to
  // 3.2:1, so they're sized by HEIGHT and capped in width, which keeps every show's mark the
  // same visual weight instead of letting the wide ones dominate the card.
  function eventTag(r) {
    if (r.event_logo) {
      return '<span class="sgr-ev-logo"><img src="' + esc(r.event_logo) + '" alt="' +
        esc(r.event_name || '') + '" title="' + esc(r.event_name || '') + '" loading="lazy" /></span>';
    }
    if (r.event_name) return '<span class="sgr-feed-event">' + esc(r.event_name) + '</span>';
    return '';   // an unmapped tag shows nothing rather than leaking the raw id
  }

  // ---- Reviews landing page (all reviews, searchable) ----
  // Card variant that names WHO the review is about — on this page reviews sit far from their
  // product, so without attribution they read as unsourced.
  // Three columns: who it's about (left), what they said (middle), what they photographed
  // (right). Full-width rows rather than masonry cards, because CSS columns fill top-to-bottom
  // per column — under "Most recent" the newest review lands top-left but the 7th lands
  // top-RIGHT, so reading order stopped matching sort order. Rows also give the unusually rich
  // metadata (headshot, signer, item, show, verified, date) somewhere to live other than
  // stacked on top of the stars, and give the photos a consistent slot instead of a variable
  // position mid-card. Collapses to stacked below 760px.
  // opts.hideSigner — on a product page the signer is the page, so repeating their name and
  // face on all 17 rows is noise. The rest of the left column still earns its place: with
  // signer-scoped reviews the SIGNING varies, so it says which one each review came from.
  function feedCard(r, faces, idx, opts) {
    opts = opts || {};
    if (opts.hideSigner) {
      var item0 = itemLabel(r);
      var photos0 = (r.photos || []).slice(0, 4);
      return '<article class="sgr-row">' +
        '<div class="sgr-row-who">' +
          (item0 ? '<div class="sgr-row-item sgr-row-item-lead">' + esc(item0) + '</div>' : '') +
          '<div class="sgr-who-by">' +
            '<span class="sgr-row-by">' + esc(r.name) + '</span>' +
            (r.verified ? '<span class="sgr-vtick" title="Verified buyer">✓ Verified</span>' : '') +
          '</div>' +
          '<div class="sgr-who-show">' +
            (function () {
              var t = eventTag(r);
              return t ? '<span class="sgr-show-mark"><span class="sgr-who-show-lbl">Signed at</span>' + t + '</span>' : '';
            })() +
            '<span class="sgr-row-date">' + esc(fmtDate(r.date)) + '</span>' +
          '</div>' +
        '</div>' +
        '<div class="sgr-row-said">' + stars(r.rating) +
          (r.title ? '<div class="sgr-title">' + esc(r.title) + '</div>' : '') +
          (r.body ? '<div class="sgr-body">' + esc(r.body) + '</div>' : '') +
          (r.reply ? '<div class="sgr-reply"><span class="sgr-reply-by">Signet responded</span>' + esc(r.reply) + '</div>' : '') +
        '</div>' +
        (photos0.length ? '<div class="sgr-row-shot">' +
          '<img class="sgr-rphoto sgr-rphoto-hero" src="' + esc(photos0[0]) + '" alt="Customer photo" loading="lazy" data-r="' + idx + '" />' +
          (photos0.length > 1 ? '<div class="sgr-rphoto-more">' + photos0.slice(1).map(function (p) {
            return '<img class="sgr-rphoto sgr-rphoto-thumb" src="' + esc(p) + '" alt="Customer photo" loading="lazy" data-r="' + idx + '" />';
          }).join('') + '</div>' : '') + '</div>' : '') +
        '</article>';
    }
    // The first photo is the hero and stretches to whatever height the row ends up being;
    // any others sit under it as small thumbs rather than three equal squares leaving a gap.
    var pics = (r.photos || []).slice(0, 4);
    var photos = pics.length
      ? '<img class="sgr-rphoto sgr-rphoto-hero" src="' + esc(pics[0]) + '" alt="Customer photo" loading="lazy" data-r="' + idx + '" />' +
        (pics.length > 1
          ? '<div class="sgr-rphoto-more">' + pics.slice(1).map(function (p) {
              return '<img class="sgr-rphoto sgr-rphoto-thumb" src="' + esc(p) + '" alt="Customer photo" loading="lazy" data-r="' + idx + '" />';
            }).join('') + '</div>'
          : '')
      : '';
    var item = itemLabel(r);
    return '<article class="sgr-row">' +
      // Two groups, not six stacked lines: WHAT was signed (signer, item, show) and WHO said
      // it (reviewer, date, verified). Related things sit tight, unrelated things get air.
      '<div class="sgr-row-who">' +
        // Name and item share a column BESIDE the avatar, so the item always tucks directly
        // under the name. Previously it was a sibling of the whole signer row, which put it
        // under the avatar and let it drift as widths changed.
        (r.signer
          // ⭐ Server-supplied face first. It is resolved by signer_id, so it works on every
          // surface — including the product page, which passes an EMPTY faces map and therefore
          // showed initials for everyone regardless of what headshots existed.
          // ⚠ The name-keyed map stays as a fallback: the all-reviews page builds a real one
          // from ?signers=1, and it still covers a review whose signer_id has no stats row.
          ? '<div class="sgr-row-signer">' + avatar(r.signer, r.signer_headshot || (faces || {})[r.signer], 'sgr-face-sm') +
              '<span class="sgr-signer-stack">' +
                '<span class="sgr-row-signer-name">' + esc(r.signer) + '</span>' +
                (item ? '<span class="sgr-row-item">' + esc(item) + '</span>' : '') +
              '</span>' +
            '</div>'
          : (item ? '<div class="sgr-row-item">' + esc(item) + '</div>' : '')) +
        '<div class="sgr-who-by">' +
          '<span class="sgr-row-by">' + esc(r.name) + '</span>' +
          (r.verified ? '<span class="sgr-vtick" title="Verified buyer">✓ Verified</span>' : '') +
        '</div>' +
        // Provenance at the foot: where it was signed, and when it was reviewed. Both are
        // context rather than identity, so they share a zone instead of padding out the
        // stack above. The foot still renders for a review with no show, so the date has a
        // consistent home either way.
        '<div class="sgr-who-show">' +
          (function () {
            var t = eventTag(r);
            return t ? '<span class="sgr-show-mark"><span class="sgr-who-show-lbl">Signed at</span>' + t + '</span>' : '';
          })() +
          '<span class="sgr-row-date">' + esc(fmtDate(r.date)) + '</span>' +
        '</div>' +
      '</div>' +
      '<div class="sgr-row-said">' +
        stars(r.rating) +
        (r.title ? '<div class="sgr-title">' + esc(r.title) + '</div>' : '') +
        (r.body ? '<div class="sgr-body">' + esc(r.body) + '</div>' : '') +
        (r.reply ? '<div class="sgr-reply"><span class="sgr-reply-by">Signet responded</span>' + esc(r.reply) + '</div>' : '') +
      '</div>' +
      (photos ? '<div class="sgr-row-shot">' + photos + '</div>' : '') +
      '</article>';
  }

  // Initials stand in when a signer has no headshot — 63% coverage, so the fallback is
  // common enough that it has to look deliberate rather than broken.
  function initials(name) {
    var p = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!p.length) return '★';
    return (p[0][0] + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
  }
  function avatar(name, url, cls) {
    return url
      ? '<img class="sgr-face ' + cls + '" src="' + esc(url) + '" alt="" loading="lazy" decoding="async" />'
      : '<span class="sgr-face sgr-face-init ' + cls + '">' + esc(initials(name)) + '</span>';
  }

  function renderAll(el) {
    var state = { q: '', signer: '', ratings: [], photos: false, sort: 'newest', cursor: null };
    var pageSize = Number(el.getAttribute('data-limit')) || 12;
    var faces = {};          // signer name -> headshot url, for the per-row avatars
    var knownSigners = [];   // every signer name, so the type-ahead only filters on real ones
    var loaded = [];  // every review rendered so far, so a photo can reopen its own review

    el.innerHTML =
      '<div class="sgr-feed">' +
        // Placeholder — filled by the same statsBar() the product page uses, once the numbers
        // arrive. Building it here by hand is what would let the two headers drift.
        '<div class="sgr-bar-slot"></div>' +
        '<div class="sgr-wall-wrap"><div class="sgr-faces-lbl">Customer photos</div>' +
          '<div class="sgr-faces-rail">' +
            '<button class="sgr-arrow sgr-wall-arrow-l" aria-label="Scroll left">‹</button>' +
            '<div class="sgr-wall"></div>' +
            '<button class="sgr-arrow sgr-wall-arrow-r" aria-label="Scroll right">›</button>' +
          '</div></div>' +
        '<div class="sgr-faces-wrap"><div class="sgr-faces-lbl">Browse by signer</div>' +
          '<div class="sgr-faces-rail">' +
            '<button class="sgr-arrow sgr-arrow-l" aria-label="Scroll left">‹</button>' +
            '<div class="sgr-faces"></div>' +
            '<button class="sgr-arrow sgr-arrow-r" aria-label="Scroll right">›</button>' +
          '</div></div>' +
        '<div class="sgr-controls">' +
          '<input class="sgr-search" type="search" placeholder="Search reviews, signers, products…" aria-label="Search reviews" />' +
          '<div class="sgr-filter-wrap">' +
            '<button class="sgr-filter-btn" aria-expanded="false">Filters<span class="sgr-filter-n"></span></button>' +
            '<div class="sgr-filter-menu" hidden>' +
              '<div class="sgr-fgroup"><div class="sgr-flabel">Rating</div><div class="sgr-fstars">' +
                [5, 4, 3, 2, 1].map(function (n) {
                  return '<label class="sgr-fcheck"><input type="checkbox" data-star="' + n + '" />' +
                    '<span>' + n + ' star' + (n === 1 ? '' : 's') + '</span></label>';
                }).join('') +
              '</div></div>' +
              // ⚠ THIS USED TO BE DELIBERATELY ABSENT, on the grounds that a signer field is "a
              // wall of text in the middle of a small menu". That objection is right about a raw
              // datalist and wrong about the need — the faces rail only reveals names by
              // scrolling, so you cannot skim who is here, and you cannot search a name you
              // haven't seen. Operator-requested 2026-08-22.
              //
              // Three things keep it from being the wall the old comment feared:
              //   • COLLAPSED by default, so the menu is unchanged until asked
              //   • a filter box, because 157 names is too many to scan but trivial to search
              //   • sorted by REVIEW COUNT, so the signers worth filtering to are at the top
              //     rather than whoever happens to start with "A"
              // ⚠ 157, not 483 — only signers who actually HAVE reviews can appear here, and that
              //   list grows far more slowly than the talent roster.
              '<div class="sgr-fgroup sgr-fsig">' +
                '<button type="button" class="sgr-fsig-toggle" aria-expanded="false">' +
                  '<span class="sgr-flabel">Signer</span>' +
                  '<span class="sgr-fsig-sel">Any</span>' +
                  '<span class="sgr-fsig-chev" aria-hidden="true">›</span>' +
                '</button>' +
                '<div class="sgr-fsig-panel" hidden>' +
                  '<input class="sgr-fsig-search" type="search" placeholder="Find a signer…" aria-label="Find a signer" />' +
                  '<div class="sgr-fsig-list" role="listbox"></div>' +
                '</div>' +
              '</div>' +
              '<div class="sgr-fgroup"><div class="sgr-flabel">Sort</div>' +
                '<select class="sgr-sort" aria-label="Sort reviews">' +
                  '<option value="newest">Most recent</option>' +
                  '<option value="highest">Highest rated</option>' +
                  '<option value="lowest">Lowest rated</option>' +
                '</select>' +
              '</div>' +
              '<label class="sgr-fcheck sgr-fphotos"><input type="checkbox" data-photos="1" /><span>Only with photos</span></label>' +
              '<div class="sgr-fbar"><button class="sgr-fclear">Clear all</button><button class="sgr-fdone">Done</button></div>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="sgr-feed-meta" aria-live="polite"></div>' +
        '<div class="sgr-feed-list"></div>' +
        '<div class="sgr-feed-foot"></div>' +
      '</div>';

    var $ = function (s) { return el.querySelector(s); };
    var list = $('.sgr-feed-list'), meta = $('.sgr-feed-meta'), foot = $('.sgr-feed-foot');

    // Customer photos of signed items are the most persuasive content on the page — 42% of
    // approved reviews carry one — and scattered one-per-card they never register as a body of
    // proof. Pulled with the existing photos=1 filter, so no new endpoint. Clicking a tile
    // opens that review's photo rather than a bare lightbox with no context.
    function loadWall() {
      var wrap = $('.sgr-wall-wrap'), wall = $('.sgr-wall');
      get(API + '?all=1&photos=1&limit=30&sort=newest').then(function (d) {
        var tiles = [];
        (d.reviews || []).forEach(function (r) {
          (r.photos || []).slice(0, 2).forEach(function (p) { tiles.push({ src: p, review: r }); });
        });
        if (tiles.length < 6) { wrap.style.display = 'none'; return; }   // too few to read as a strip
        wall.innerHTML = tiles.map(function (t, i) {
          return '<button class="sgr-wall-tile" data-i="' + i + '" title="' + esc(t.review.signer || '') + '">' +
            '<img src="' + esc(t.src) + '" alt="Customer photo of a signed item" loading="lazy" />' +
            (t.review.signer ? '<span class="sgr-wall-cap">' + esc(t.review.signer) + '</span>' : '') + '</button>';
        }).join('');
        wall.querySelectorAll('.sgr-wall-tile').forEach(function (b) {
          b.addEventListener('click', function () {
            var n = Number(b.getAttribute('data-i'));
            var t = tiles[n];
            // ⭐ Arrow between photos without closing and re-opening — operator-requested
            // 2026-08-22, same affordance as the homepage rail. `tiles` is the whole wall, so
            // next/prev walks the strip in the order it is displayed. The review still travels
            // with the photo, so the words change with the picture.
            lightbox(t.src, t.review, undefined, { list: tiles, index: n });
          });
        });
        wireRail(wall, $('.sgr-wall-arrow-l'), $('.sgr-wall-arrow-r'));
      }).catch(function () { wrap.style.display = 'none'; });
    }

    function setSigner(name) {
      state.signer = name;
      if (typeof syncBadge === 'function') syncBadge();
      el.querySelectorAll('.sgr-face-btn').forEach(function (b) {
        var on = b.getAttribute('data-signer') === name;
        b.classList.toggle('sgr-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      // ⚠ The faces rail and the filter list are two views of ONE piece of state. Selecting in
      // either must light up the other, or the menu can say "Any" while a face is clearly
      // active — which reads as a bug even though the results are right.
      syncSignerFilter();
      reload();
    }

    // Reflect state.signer into the filter list: the collapsed summary and the row highlight.
    function syncSignerFilter() {
      var sel = el.querySelector('.sgr-fsig-sel');
      if (sel) sel.textContent = state.signer || 'Any';
      if (sel) sel.classList.toggle('sgr-fsig-on', !!state.signer);
      el.querySelectorAll('.sgr-fsig-opt').forEach(function (b) {
        var on = b.getAttribute('data-signer') === state.signer;
        b.classList.toggle('sgr-on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    function buildSignerFilter(list) {
      var wrap = el.querySelector('.sgr-fsig');
      var listEl = el.querySelector('.sgr-fsig-list');
      var search = el.querySelector('.sgr-fsig-search');
      var toggle = el.querySelector('.sgr-fsig-toggle');
      var panel = el.querySelector('.sgr-fsig-panel');
      if (!wrap || !listEl || !list.length) { if (wrap) wrap.style.display = 'none'; return; }

      // "Any" first so clearing is a click rather than a hunt for the active row.
      listEl.innerHTML =
        '<button type="button" class="sgr-fsig-opt sgr-fsig-any" data-signer="" role="option" aria-selected="true">' +
          '<span class="sgr-fsig-name">Any signer</span></button>' +
        list.map(function (s) {
          return '<button type="button" class="sgr-fsig-opt" data-signer="' + esc(s.signer) + '" role="option" aria-selected="false">' +
            '<span class="sgr-fsig-name">' + esc(s.signer) + '</span>' +
            '<span class="sgr-fsig-n">' + (s.count || 0) + '</span></button>';
        }).join('');

      toggle.addEventListener('click', function () {
        var open = panel.hasAttribute('hidden');
        if (open) { panel.removeAttribute('hidden'); search.focus(); }
        else panel.setAttribute('hidden', '');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        wrap.classList.toggle('sgr-open', open);
      });

      // Substring, not prefix — people search for a surname far more often than a full name.
      search.addEventListener('input', function () {
        var q = search.value.trim().toLowerCase();
        var shown = 0;
        listEl.querySelectorAll('.sgr-fsig-opt').forEach(function (b) {
          if (b.classList.contains('sgr-fsig-any')) { b.style.display = q ? 'none' : ''; return; }
          var hit = b.getAttribute('data-signer').toLowerCase().indexOf(q) !== -1;
          b.style.display = hit ? '' : 'none';
          if (hit) shown++;
        });
        var none = el.querySelector('.sgr-fsig-none');
        if (none) none.remove();
        if (q && !shown) listEl.insertAdjacentHTML('beforeend', '<div class="sgr-fsig-none">No signer matches “' + esc(search.value.trim()) + '”</div>');
      });
      // Enter picks the only remaining match — the natural end of typing a name.
      search.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        var vis = [].slice.call(listEl.querySelectorAll('.sgr-fsig-opt')).filter(function (b) { return b.style.display !== 'none'; });
        if (vis.length === 1) vis[0].click();
      });

      listEl.addEventListener('click', function (e) {
        var b = e.target.closest && e.target.closest('.sgr-fsig-opt');
        if (!b) return;
        setSigner(b.getAttribute('data-signer') || '');
      });

      syncSignerFilter();
    }

    // One call fills the stats bar, the face strip and the dropdown — the merged people list,
    // so one entry covers all of a signer's rotating per-event products.
    // The FEED WAITS on this: cards draw the signer's avatar from `faces`, so rendering before
    // it resolves silently falls back to initials on every card. .catch keeps a failed lookup
    // from blocking the reviews themselves.
    var signersReady = get(API + '?signers=1').then(function (d) {
      var list = d.signers || [];
      list.forEach(function (s) { if (s.headshot) faces[s.signer] = s.headshot; });

      // ── Signer filter list ───────────────────────────────────────────────────
      // The API already returns these ordered by review count, which is the order we
      // want — the names worth filtering to sit at the top instead of whoever is
      // alphabetically first. The search box handles finding a specific person, so
      // there is no reason to re-sort alphabetically and bury the useful ones.
      buildSignerFilter(list);

      // Same builder as the product page. No identity image here — the landing page's subject
      // is the shop, not a person.
      $('.sgr-bar-slot').innerHTML = statsBar(
        el.getAttribute('data-heading') || 'Customer Reviews',
        'Verified buyers, in their own words',
        d.average,
        [{ n: d.total_reviews || 0, label: 'reviews' }, { n: d.total_signers || 0, label: 'signers reviewed' }],
        false
      );

      // Faces first: the people with the most reviews, and only ones with a real photo —
      // a strip of initials circles would look like missing images rather than a feature.
      var strip = $('.sgr-faces');
      var withPics = list.filter(function (s) { return s.headshot; }).slice(0, 14);
      if (!withPics.length) { $('.sgr-faces-wrap').style.display = 'none'; }
      else {
        strip.innerHTML = withPics.map(function (s) {
          return '<button class="sgr-face-btn" data-signer="' + esc(s.signer) + '" aria-pressed="false"' +
            ' aria-label="Show reviews for ' + esc(s.signer) + '" title="' + esc(s.signer) + '">' +
            avatar(s.signer, s.headshot, 'sgr-face-lg') +
            '<span class="sgr-face-name">' + esc(s.signer.split(/\s+/)[0]) + '</span>' +
            '<span class="sgr-face-n">' + s.count + '</span></button>';
        }).join('');
        strip.querySelectorAll('.sgr-face-btn').forEach(function (b) {
          b.addEventListener('click', function () {
            var n = b.getAttribute('data-signer');
            setSigner(state.signer === n ? '' : n);   // click again to clear
          });
        });

        wireRail(strip, $('.sgr-arrow-l'), $('.sgr-arrow-r'));
      }

      // Still tracked so the deep-link (?signer=) and the faces rail can validate a name,
      // even though there's no longer a field listing them.
      knownSigners = list.map(function (s) { return s.signer; });
    }).catch(function () { /* no faces — cards fall back to initials */ });

    function query(append) {
      var u = API + '?all=1&limit=' + pageSize + '&sort=' + encodeURIComponent(state.sort);
      if (state.q) u += '&q=' + encodeURIComponent(state.q);
      if (state.signer) u += '&signer_name=' + encodeURIComponent(state.signer);
      if (state.ratings.length) u += '&ratings=' + state.ratings.join(',');
      if (state.photos) u += '&photos=1';
      if (append && state.cursor) u += '&cursor=' + encodeURIComponent(state.cursor);

      // Re-filtering keeps the header in place, so only the rows are placeholdered.
      if (!append) { list.innerHTML = skeleton(4, { bar: false }); foot.innerHTML = ''; }
      return get(u).then(function (d) {
        var items = d.reviews || [];
        if (!append) { list.innerHTML = ''; loaded.length = 0; }
        if (!items.length && !append) {
          list.innerHTML = '<div class="sgr-empty">No reviews match that. Try clearing a filter.</div>';
          meta.textContent = '';
          return;
        }
        // Index into the accumulated set so a row's photo can reopen ITS review in the
        // lightbox, including after Load more.
        var base = loaded.length;
        items.forEach(function (r) { loaded.push(r); });
        list.insertAdjacentHTML('beforeend', items.map(function (r, i) { return feedCard(r, faces, base + i); }).join(''));
        state.cursor = d.next_cursor;
        meta.textContent = (d.total != null ? d.total + ' matching review' + (d.total === 1 ? '' : 's') : '')
          + (state.signer ? ' · ' + state.signer : '');
        foot.innerHTML = d.next_cursor ? '<button class="sgr-more">Load more reviews</button>' : '';
        var more = $('.sgr-more');
        if (more) more.addEventListener('click', function () { more.disabled = true; more.textContent = 'Loading…'; query(true); });
        list.querySelectorAll('.sgr-rphoto').forEach(function (img) {
          if (img.getAttribute('data-bound')) return;
          img.setAttribute('data-bound', '1');
          img.addEventListener('click', function () {
            lightbox(img.getAttribute('src'), loaded[Number(img.getAttribute('data-r'))]);
          });
        });
      }).catch(function () {
        if (!append) list.innerHTML = '<div class="sgr-empty">Reviews are unavailable right now.</div>';
      });
    }

    function reload() { state.cursor = null; query(false); }

    // Debounced so typing doesn't fire a request per keystroke.
    var t;
    $('.sgr-search').addEventListener('input', function (e) {
      clearTimeout(t);
      var v = e.target.value;
      t = setTimeout(function () { state.q = v.trim(); reload(); }, 300);
    });
    $('.sgr-sort').addEventListener('change', function (e) { state.sort = e.target.value; syncBadge(); reload(); });

    // One menu instead of a row of preset chips. Presets could only express what we thought
    // of — "5 only" or "4 and up" — so a shopper wanting 3-star reviews with photos had no way
    // to ask. Any combination of stars, signer, photos and sort is now reachable.
    var fbtn = $('.sgr-filter-btn'), fmenu = $('.sgr-filter-menu');
    function openMenu(on) {
      fmenu.hidden = !on;
      fbtn.setAttribute('aria-expanded', on ? 'true' : 'false');
      fbtn.classList.toggle('sgr-on', on);
      // Open upward when there isn't room below. The Filters button sits above a long feed, so
      // by the time someone has scrolled to it the button is often near the bottom of the
      // viewport — and a menu anchored to its top then runs off the screen with no way to
      // reach Done. Measured after unhiding, because a hidden element has no height.
      if (!on) { fmenu.classList.remove('sgr-menu-up'); return; }
      fmenu.classList.remove('sgr-menu-up');
      var btn = fbtn.getBoundingClientRect();
      var needed = fmenu.offsetHeight + 12;
      var below = window.innerHeight - btn.bottom;
      if (below < needed && btn.top > below) fmenu.classList.add('sgr-menu-up');
    }
    function activeCount() {
      return state.ratings.length + (state.photos ? 1 : 0) + (state.signer ? 1 : 0) + (state.sort !== 'newest' ? 1 : 0);
    }
    function syncBadge() {
      var n = activeCount();
      $('.sgr-filter-n').textContent = n ? String(n) : '';
      fbtn.classList.toggle('sgr-has', n > 0);
    }
    fbtn.addEventListener('click', function (e) { e.stopPropagation(); openMenu(fmenu.hidden); });
    // A popover you can open with the keyboard but not close with it is a trap.
    fmenu.addEventListener('keydown', function (e) { if (e.key === 'Escape') { openMenu(false); fbtn.focus(); } });
    fbtn.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !fmenu.hidden) { openMenu(false); } });
    fmenu.addEventListener('click', function (e) { e.stopPropagation(); });
    document.addEventListener('click', function () { openMenu(false); });
    $('.sgr-fdone').addEventListener('click', function () { openMenu(false); });

    el.querySelectorAll('.sgr-fstars input[data-star]').forEach(function (c) {
      c.addEventListener('change', function () {
        var v = Number(c.getAttribute('data-star'));
        var i = state.ratings.indexOf(v);
        if (c.checked && i === -1) state.ratings.push(v);
        else if (!c.checked && i !== -1) state.ratings.splice(i, 1);
        syncBadge(); reload();
      });
    });
    $('.sgr-fphotos input').addEventListener('change', function (e) {
      state.photos = e.target.checked; syncBadge(); reload();
    });
    $('.sgr-fclear').addEventListener('click', function () {
      state.ratings = []; state.photos = false; state.signer = ''; state.sort = 'newest'; state.q = '';
      el.querySelectorAll('.sgr-filter-menu input[type=checkbox]').forEach(function (x) { x.checked = false; });
      $('.sgr-sort').value = 'newest'; $('.sgr-search').value = '';
      el.querySelectorAll('.sgr-face-btn').forEach(function (b) { b.classList.remove('sgr-on'); });
      // ⚠ The signer list is a third view of state.signer — clearing the state without
      // resetting it leaves the summary reading a name that is no longer filtered.
      var sigSearch = $('.sgr-fsig-search');
      if (sigSearch) { sigSearch.value = ''; sigSearch.dispatchEvent(new Event('input')); }
      syncSignerFilter();
      syncBadge(); reload();
    });

    // Honour ?signer= / ?rating= from the URL, so a product page can deep-link here
    // pre-filtered and the view is shareable.
    try {
      var qp = new URLSearchParams(window.location.search);
      var qsSigner = (qp.get('signer') || '').trim();
      var qsRating = Number(qp.get('rating'));
      // Deep-link still works — the faces rail marks the match once it loads, and there's no
      // longer a field to mirror the value into.
      if (qsSigner) { state.signer = qsSigner; }
      if (qsRating >= 1 && qsRating <= 5) {
        state.ratings = [qsRating];
        var box = el.querySelector('.sgr-fstars input[data-star="' + qsRating + '"]');
        if (box) box.checked = true;
      }
      if (qsSigner || state.ratings.length) syncBadge();
    } catch (e) { /* no URLSearchParams — filters just start empty */ }

    // First paint waits on the faces: cards draw the signer avatar from `faces`, so rendering
    // before that resolves silently falls back to initials on every card.
    signersReady.then(reload);
    loadWall();
  }

  function init() {
    document.querySelectorAll('[data-signet-reviews][data-product-id]').forEach(renderProduct);
    document.querySelectorAll('[data-signet-reviews-summary]').forEach(renderSummary);
    document.querySelectorAll('[data-signet-reviews-all]').forEach(renderAll);
    renderBadges();
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
